import { supabase } from '../utils/supabase';

export const USER_ROLES = {
  SAAS_ADMIN: 'saasadmin',
  OWNER: 'owner',
  MEMBER: 'member'
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [USER_ROLES.SAAS_ADMIN]: 'SaaS Admin',
  [USER_ROLES.OWNER]: 'Coordenador',
  [USER_ROLES.MEMBER]: 'Membro'
};

export const ROLE_COLORS: Record<
  UserRole,
  'blue' | 'green' | 'red' | 'yellow' | 'gray' | 'indigo' | 'purple' | 'pink'
> = {
  [USER_ROLES.SAAS_ADMIN]: 'purple',
  [USER_ROLES.OWNER]: 'blue',
  [USER_ROLES.MEMBER]: 'green'
};

interface InviteUserParams {
  email: string;
  organizationId: string;
  role?: UserRole;
}

interface InviteUserResponse {
  success: boolean;
  message: string;
  data?: any;
  error?: Error;
}

interface RoleOption {
  value: UserRole;
  label: string;
}

export class InvitationService {
  static async inviteUser({
    email,
    organizationId,
    role = USER_ROLES.OWNER
  }: InviteUserParams): Promise<InviteUserResponse> {
    if (!Object.values(USER_ROLES).includes(role)) {
      throw new Error(
        `Invalid role: ${role}. Must be one of: ${Object.values(USER_ROLES).join(', ')}`
      );
    }

    if (!email || !organizationId) {
      throw new Error('Email and organization ID are required');
    }

    try {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email,
          organizationId,
          role
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      return {
        success: true,
        message: data.message || `Convite enviado para ${email}`,
        data
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  static getRoleDisplayName(role: UserRole): string {
    return ROLE_DISPLAY_NAMES[role] || role;
  }

  static getAvailableRoles(): UserRole[] {
    return Object.values(USER_ROLES);
  }

  static getRoleOptions(): RoleOption[] {
    return Object.entries(ROLE_DISPLAY_NAMES).map(([value, label]) => ({
      value: value as UserRole,
      label
    }));
  }
}
